import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Trash2 } from "lucide-react";

const STATUSES = ["new", "contacted", "converted", "closed"];

export default function AdminLeads() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { data } = useQuery({
    queryKey: ["leads", q, statusFilter],
    queryFn: () => api.get("/admin/leads", { params: { search: q || undefined, status: statusFilter || undefined } }).then((r) => r.data),
  });
  const leads = data?.items || [];

  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => api.put(`/admin/leads/${id}`, { status }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/leads/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Deleted"); },
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600">Inbox</div>
          <h1 className="font-display text-3xl text-slate-950 mt-1">Leads · {data?.total ?? 0}</h1>
        </div>
        <div className="flex gap-3 items-center">
          <select className="h-10 border rounded-md px-3 bg-white border-slate-300 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="lead-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 w-64" data-testid="leads-search" />
          </div>
        </div>
      </div>

      <Card className="border border-slate-200"><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Lead ID</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Contact</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Travel</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Source</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Received</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{l.lead_code}</td>
                  <td className="px-4 py-3"><div className="text-slate-900 font-medium">{l.name}</div><div className="text-slate-500 text-xs">{l.city}</div></td>
                  <td className="px-4 py-3"><div>{l.mobile}</div><div className="text-slate-500 text-xs">{l.email}</div></td>
                  <td className="px-4 py-3"><div>{l.travel_date || "—"}</div><div className="text-slate-500 text-xs">{l.travellers ? `${l.travellers} travellers` : ""}</div></td>
                  <td className="px-4 py-3"><Badge variant="outline">{l.source_ref || l.source}</Badge></td>
                  <td className="px-4 py-3">
                    <select value={l.status || "new"} onChange={(e) => updateStatus.mutate({ id: l.id, status: e.target.value })} className="h-8 border rounded-md px-2 text-xs bg-white border-slate-300" data-testid={`lead-status-${l.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="icon" onClick={() => { if (window.confirm("Delete lead?")) del.mutate(l.id); }} data-testid={`lead-delete-${l.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={99} className="text-center py-16 text-slate-400">No leads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
    </div>
  );
}
